import React from 'react';

type PublicPageSlug = 'home' | 'about' | 'gallery' | 'prices' | 'education' | 'contact' | 'account';

export type PublicSitePage = {
  slug: PublicPageSlug | string;
  route: string;
  nav_label: string;
  browser_title: string;
  meta_description: string;
  meta_keywords: string;
  show_in_web: number;
  is_system: number;
  sort_order: number;
};

type PublicSiteSettings = {
  site_name?: string;
  site_tagline?: string;
  default_meta_description?: string;
  default_meta_keywords?: string;
};

type SiteSettingsContextValue = {
  isReady: boolean;
  settings: PublicSiteSettings;
  pages: PublicSitePage[];
  getPageBySlug: (slug: string) => PublicSitePage | undefined;
  getPageByRoute: (route: string) => PublicSitePage | undefined;
  isPageVisible: (slug: string) => boolean;
};

const fallbackPages: PublicSitePage[] = [
  { slug: 'home', route: '/', nav_label: 'Portada', browser_title: 'DentalPuente | Ortodoncia y Odontología en Guadalajara', meta_description: 'DentalPuente: odontología profesional, ortodoncia, brackets y atención dental integral en Guadalajara y Plaza La Gigantera.', meta_keywords: 'dentista guadalajara, ortodoncia, brackets, dentalpuente, plaza la gigantera', show_in_web: 1, is_system: 1, sort_order: 10 },
  { slug: 'about', route: '/About', nav_label: 'Acerca de', browser_title: 'DentalPuente | Acerca de', meta_description: 'Conoce al equipo clínico de DentalPuente y nuestra forma de atención dental.', meta_keywords: 'odontologos, dentistas, dentalpuente', show_in_web: 1, is_system: 0, sort_order: 20 },
  { slug: 'gallery', route: '/Gallery', nav_label: 'Galería', browser_title: 'DentalPuente | Galería', meta_description: 'Galería de instalaciones, consultorio y resultados de DentalPuente.', meta_keywords: 'galeria dental, resultados, consultorio dental', show_in_web: 1, is_system: 0, sort_order: 30 },
  { slug: 'prices', route: '/Prices', nav_label: 'Precios', browser_title: 'DentalPuente | Precios', meta_description: 'Consulta precios estimados de tratamientos dentales en DentalPuente.', meta_keywords: 'precios dentales, tratamientos dentales, ortodoncia', show_in_web: 1, is_system: 0, sort_order: 40 },
  { slug: 'education', route: '/InformacionDental', nav_label: 'Información Dental', browser_title: 'Información Dental | DentalPuente', meta_description: 'Biblioteca dental de DentalPuente con guías sobre endodoncia, ortodoncia, brackets, caries, prevención y urgencias dentales.', meta_keywords: 'información dental, endodoncia, ortodoncia, brackets, caries, dentista guadalajara', show_in_web: 1, is_system: 0, sort_order: 45 },
  { slug: 'contact', route: '/Contact', nav_label: 'Contacto', browser_title: 'DentalPuente | Contacto', meta_description: 'Agenda una cita en DentalPuente Teodomiro Manzano o Plaza La Gigantera.', meta_keywords: 'contacto dentalpuente, cita dental, dentista guadalajara, plaza la gigantera', show_in_web: 1, is_system: 0, sort_order: 50 },
  { slug: 'account', route: '/Account', nav_label: 'Acceso', browser_title: 'DentalPuente | Acceso', meta_description: 'Acceso privado al portal DentalPuente.', meta_keywords: 'acceso dentalpuente', show_in_web: 1, is_system: 1, sort_order: 90 },
];

const fallbackSettings: PublicSiteSettings = {
  site_name: 'DentalPuente',
  site_tagline: 'Odontología profesional en Guadalajara',
  default_meta_description: 'DentalPuente: odontología profesional, ortodoncia, brackets y atención dental integral en Guadalajara y Plaza La Gigantera.',
  default_meta_keywords: 'dentista guadalajara, ortodoncia, brackets, dentalpuente, plaza la gigantera',
};

const SiteSettingsContext = React.createContext<SiteSettingsContextValue>({
  isReady: false,
  settings: fallbackSettings,
  pages: fallbackPages,
  getPageBySlug: (slug: string) => fallbackPages.find((page) => page.slug === slug),
  getPageByRoute: (route: string) => fallbackPages.find((page) => page.route.toLowerCase() === route.toLowerCase()),
  isPageVisible: (slug: string) => {
    const page = fallbackPages.find((item) => item.slug === slug);
    return !page || page.is_system === 1 || page.show_in_web === 1;
  },
});

const normalizePages = (pages: PublicSitePage[]): PublicSitePage[] => {
  const map = new Map<string, PublicSitePage>();
  fallbackPages.forEach((page) => map.set(page.slug, page));
  pages.forEach((page) => {
    if (!page || !page.slug || !page.route) return;
    map.set(String(page.slug), {
      ...page,
      show_in_web: Number(page.show_in_web) === 1 ? 1 : 0,
      is_system: Number(page.is_system) === 1 ? 1 : 0,
      sort_order: Number(page.sort_order) || 100,
    });
  });
  return Array.from(map.values()).sort((a, b) => (a.sort_order || 100) - (b.sort_order || 100));
};

export const SiteSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isReady, setIsReady] = React.useState(false);
  const [settings, setSettings] = React.useState<PublicSiteSettings>(fallbackSettings);
  const [pages, setPages] = React.useState<PublicSitePage[]>(fallbackPages);

  React.useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    fetch('/admin/api/site_settings_public.php', { signal: controller.signal, cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (!mounted) return;
        setSettings({ ...fallbackSettings, ...(data.settings || {}) });
        setPages(normalizePages(Array.isArray(data.pages) ? data.pages : []));
      })
      .catch(() => {
        if (!mounted) return;
        setSettings(fallbackSettings);
        setPages(fallbackPages);
      })
      .finally(() => mounted && setIsReady(true));
    return () => { mounted = false; controller.abort(); };
  }, []);

  const value = React.useMemo<SiteSettingsContextValue>(() => ({
    isReady,
    settings,
    pages,
    getPageBySlug: (slug: string) => pages.find((page) => page.slug === slug),
    getPageByRoute: (route: string) => pages.find((page) => page.route.toLowerCase() === route.toLowerCase()),
    isPageVisible: (slug: string) => {
      const page = pages.find((item) => item.slug === slug);
      return !page || page.is_system === 1 || page.show_in_web === 1;
    },
  }), [isReady, settings, pages]);

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
};

export const useSiteSettings = () => React.useContext(SiteSettingsContext);

export const publicRouteSlugs: Record<string, PublicPageSlug> = {
  '/': 'home',
  '/About': 'about',
  '/Gallery': 'gallery',
  '/Prices': 'prices',
  '/InformacionDental': 'education',
  '/Contact': 'contact',
  '/Account': 'account',
};
